import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Progress,
  Alert,
  AlertIcon,
  AlertDescription,
  Badge,
  Icon,
  Box,
} from '@chakra-ui/react';
import { FiDownload, FiPackage, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { buildCarnetZip, getSchoolInfo } from 'utils/carnetUtils';

const BatchCarnetDownload = ({ customers, onClose }) => {
  const [status, setStatus]   = useState('idle'); // idle | generating | done | error
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  const total = customers.length;

  const handleGenerate = async () => {
    setStatus('generating');
    setProgress(0);
    try {
      const schoolInfo = getSchoolInfo();
      await buildCarnetZip(customers, schoolInfo, (done, _total) => {
        setProgress(done);
      });
      setStatus('done');
    } catch (e) {
      console.error('Batch carnet error:', e);
      setErrorMsg('Ocurrió un error al generar los carnets. Intenta nuevamente.');
      setStatus('error');
    }
  };

  const pct = total > 0 ? Math.round((progress / total) * 100) : 0;

  return (
    <Modal isOpen onClose={status === 'generating' ? undefined : onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={FiPackage} color="blue.500" />
            <Text>Descargar Carnets en ZIP</Text>
          </HStack>
        </ModalHeader>
        {status !== 'generating' && <ModalCloseButton />}

        <ModalBody>
          <VStack spacing={4} align="stretch">
            {status === 'idle' && (
              <>
                <Text fontSize="sm" color="gray.600">
                  Se generará un archivo ZIP con los carnets en imagen PNG de los siguientes
                  alumnos:
                </Text>
                <HStack>
                  <Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="full">
                    {total} alumno{total !== 1 ? 's' : ''}
                  </Badge>
                </HStack>
                <Alert status="info" borderRadius="8px" fontSize="sm">
                  <AlertIcon />
                  <AlertDescription>
                    Cada carnet incluye nombre, curso, RUT y código QR del alumno.
                    El proceso puede tardar unos segundos.
                  </AlertDescription>
                </Alert>
              </>
            )}

            {status === 'generating' && (
              <VStack spacing={3} align="stretch">
                <Text fontSize="sm" color="gray.600" textAlign="center">
                  Generando carnet {progress} de {total}…
                </Text>
                <Progress
                  value={pct}
                  colorScheme="blue"
                  borderRadius="full"
                  hasStripe
                  isAnimated
                  size="md"
                />
                <Text fontSize="xs" color="gray.500" textAlign="center">
                  {pct}% completado
                </Text>
              </VStack>
            )}

            {status === 'done' && (
              <VStack spacing={3} align="center" py={2}>
                <Icon as={FiCheckCircle} color="green.500" boxSize={10} />
                <Text fontWeight="semibold" color="green.700">
                  ¡ZIP descargado correctamente!
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Se generaron {total} carnet{total !== 1 ? 's' : ''} en el archivo{' '}
                  <Box as="span" fontFamily="mono" fontWeight="semibold">
                    carnets-estudiantiles.zip
                  </Box>
                </Text>
              </VStack>
            )}

            {status === 'error' && (
              <Alert status="error" borderRadius="8px">
                <AlertIcon as={FiAlertCircle} />
                <AlertDescription fontSize="sm">{errorMsg}</AlertDescription>
              </Alert>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter gap={2}>
          {status === 'idle' && (
            <>
              <Button variant="ghost" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="blue"
                leftIcon={<FiDownload />}
                onClick={handleGenerate}
              >
                Generar y descargar ZIP
              </Button>
            </>
          )}
          {status === 'generating' && (
            <Button variant="ghost" isDisabled>
              Generando…
            </Button>
          )}
          {(status === 'done' || status === 'error') && (
            <>
              {status === 'error' && (
                <Button
                  colorScheme="blue"
                  leftIcon={<FiDownload />}
                  onClick={handleGenerate}
                >
                  Reintentar
                </Button>
              )}
              <Button onClick={onClose}>Cerrar</Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BatchCarnetDownload;
